import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import PropertiesSearchForm from 'components/dashboard/Properties/PropertiesSearchForm';
import PropertiesListSection from 'components/dashboard/Properties/PropertiesListSection';
import './PropertiesPage.scss';
import useProperties from '@/hooks/useProperties';
import usePropertyPageOptions from '@/hooks/usePropertyPageOptions'; // Renamed import
import UIPageTitle from '@/components/shared/UIPageTitle';
import UIButton from '@/components/shared/UIButton';
// import { FilterConfig } from '@/types/ui'; // Removed as it's not used

const BackToDashboardButton = () => {
  const navigate = useNavigate();
  return (
    <UIButton
      outline={true}
      variant="primary"
      size="medium"
      onClick={() => navigate('/dashboard')}
    >
      Back to Dashboard
    </UIButton>
  );
};

const AddPropertyButton = () => {
  const navigate = useNavigate();
  return (
    <UIButton
      variant="primary"
      size="medium"
      onClick={() => navigate('/dashboard/properties/add')}
    >
      + Add property
    </UIButton>
  );
};

// More flexible filters state
interface PropertiesFilters {
  [key: string]: any;
  page?: number;
  limit?: number;
  sortColumn?: string;
  sortDirection?: 'asc' | 'desc';
}

const PropertiesPage = () => {
  // const navigate = useNavigate(); // Removed as it's not used directly in this component's main scope
  const [searchParams, setSearchParams] = useSearchParams();
  
  const { optionsData, isLoading: isLoadingFilters, error: filtersError } = usePropertyPageOptions('properties'); // Updated hook usage
  const filtersConfig = optionsData?.filters || []; // Extract filtersConfig from optionsData

  const [filters, setFilters] = useState<PropertiesFilters>(() => {
    const initialFilters: PropertiesFilters = {
      page: parseInt(searchParams.get('page') || '1'),
      limit: parseInt(searchParams.get('limit') || '10'),
      sortColumn: searchParams.get('sortColumn') || undefined,
      sortDirection: (searchParams.get('sortDirection') as 'asc' | 'desc') || undefined,
    };
    
    // Initialize filters from URL, but ignore the 'search' parameter
    searchParams.forEach((value, key) => {
      if (key !== 'search') {
        initialFilters[key] = value;
      }
    });

    return initialFilters;
  });

  const { // Destructure the return value of useProperties
    ownedProperties,
    isLoading: isLoadingProperties,
    isFetching,
    totalItems,
    totalPages,
    hasNext,
    hasPrev,
  } = useProperties(filters);

  useEffect(() => {
    const newSearchParams = new URLSearchParams();
    
    for (const key in filters) {
      if (Object.prototype.hasOwnProperty.call(filters, key)) {
        const value = filters[key];
        
        // Do not write the 'search' key to the URL
        if (key === 'search') continue;

        // Always write the 'page' key to the URL
        if (key === 'page') {
          newSearchParams.set(key, String(value));
        } else if (key === 'limit' && value !== 10) {
          newSearchParams.set(key, String(value));
        } else if (key !== 'page' && key !== 'limit' && value) {
          newSearchParams.set(key, String(value));
        }
      }
    }

    setSearchParams(newSearchParams);
  }, [filters, setSearchParams]);

  // const handleAddProperty = () => { // Removed as it's not used
  //   navigate('/dashboard/properties/add');
  // };

  const handleSearch = (searchData: { [key: string]: string }) => {
    setFilters(prev => ({
      ...prev,
      ...searchData,
      page: 1, 
    }));
  };

  const handlePageChange = (page: number) => {
    setFilters(prev => ({ ...prev, page }));
  };

  const handleSort = (column: string, direction: 'asc' | 'desc') => {
    setFilters(prev => ({
      ...prev,
      sortColumn: column,
      sortDirection: direction,
      page: 1,
    }));
  };
  
  const initialFilterValues = useMemo(() => {
    const values: { [key: string]: string } = {};
    filtersConfig.forEach(filter => {
      values[filter.key] = filters[filter.key] || '';
    });
    return values;
  }, [filters, filtersConfig]);

  return (
    <>
      <UIPageTitle
        title="Properties"
        action={
          <div style={{ display: 'flex', gap: '8px' }}>
            <AddPropertyButton />
            <BackToDashboardButton />
          </div>
        }
      />

      <PropertiesSearchForm
        filtersConfig={filtersConfig}
        initialValues={initialFilterValues}
        onSearch={handleSearch}
        isLoading={isLoadingFilters}
        error={filtersError}
      />

      <PropertiesListSection
        properties={ownedProperties}
        isLoading={isLoadingProperties || isLoadingFilters}
        isFetching={isFetching}
        // onAddProperty={handleAddProperty} // Удаляем пропс
        totalItems={totalItems}
        currentPage={filters.page || 1}
        itemsPerPage={filters.limit || 10}
        totalPages={totalPages}
        hasNext={hasNext}
        hasPrev={hasPrev}
        onPageChange={handlePageChange}
        sortColumn={filters.sortColumn}
        sortDirection={filters.sortDirection}
        onSort={handleSort}
      />
    </>
  );
};

export default PropertiesPage;
